import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser, Platform } from '@app/classes';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { RoutingStateService } from '@app/modules/common/services/routing-state.service';
import { AbilityService, AnalyticService, AuthService, NotifyService } from '@app/services';
import { EmployeeRoeService, RehireService, TerminationService } from '@employees/services';
import { Employee } from '@models/employee/employee.model';
import moment from 'moment';
import { EmployeeMenuService } from '../../services/employee-menu.service';

@Component({
    selector: 'app-employee-header',
    templateUrl: './employee-header.template.html',
    styleUrls: ['./employee-header.style.scss'],
})
export class EmployeeHeaderComponent implements OnInit {
    @ViewChild('confirmDeleteDialog', { static: true })
    confirmDeleteDialog: DeprecatedConfirmationDialogComponent;

    @ViewChild('confirmReallyDeleteDialog', { static: true })
    confirmReallyDeleteDialog: DeprecatedConfirmationDialogComponent;

    @ViewChild('confirmRehire', { static: true })
    confirmRehireDialog: DeprecatedConfirmationDialogComponent;

    @Input() set employee(employee: Employee) {
        this._employee = employee;
        this.employeeMenuService.employee = employee;
    }
    get employee(): Employee | undefined {
        return this._employee;
    }

    @Input() terminateUrl: string[] = ['..', 'terminate'];
    @Input() withMargin = true;

    @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

    placeOnLeaveUrl: string[] = ['..', 'leave-of-absence'];

    isSelf = false;
    isAdmin = false;
    employeeHasUpcomingTermination = false;
    employeeHasUpcomingRehire = false;
    isLeaveOfAbsenceDetailsModalOpen = false;

    deleteEmployeePermission: string[] = Employee.permission.edit;
    issueRoePermission: string[] = Platform.payroll.permission.view;
    hasPayrollFeature: boolean;
    hasManageLeaveOfAbsence = false;
    leaveOfAbsenceScheduled = false;

    backRoute = '/employees/directory';

    private _employee?: Employee;

    constructor(
        public auth: AuthService,
        public route: ActivatedRoute,
        private notify: NotifyService,
        private router: Router,
        private abilities: AbilityService,
        private employeeRoeService: EmployeeRoeService,
        private terminationService: TerminationService,
        private rehireService: RehireService,
        private analyticService: AnalyticService,
        private routingStateService: RoutingStateService,
        public employeeMenuService: EmployeeMenuService
    ) {
        this.hasPayrollFeature = this.abilities.payroll();
    }

    async ngOnInit(): Promise<void> {
        this.isAdmin = this.auth.isAdmin();
        this.isSelf = this.auth.isSelf(this.employee);
        this.employeeHasUpcomingTermination = this.employee.hasUpcomingTermination();
        this.employeeHasUpcomingRehire = this.employee.hasUpcomingRehire();
        this.hasManageLeaveOfAbsence = this.auth.can(Employee.manageLeaveOfAbsencePermission);

        if (!this.employee.onLeave && this.employee.leaveOfAbsences.length > 0) {
            // Want the latest leave of absence
            const currentLeaveOfAbsence = this.employee?.leaveOfAbsences[0];
            this.leaveOfAbsenceScheduled = moment(currentLeaveOfAbsence.start).isAfter(moment(), 'day');
        }

        this.backRoute = this.getBackRoute();
    }

    resendAccountInvite(employee: Employee): void {
        this.isLoading.next(true);
        this.auth
            .sendInvitations([employee.accountId], this.auth.company.id)
            .then(() => {
                this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileMeatballResendInvite);
                this.notify.success('employees.header.accountInvitationSent');
                this.isLoading.next(false);
            })
            .catch((err) => {
                this.notify.error(err.message || 'employees.header.unableToSendInvite');
                this.isLoading.next(false);
            });
    }

    onDelete(): void {
        this.confirmDeleteDialog.show().then((confirm) => {
            if (!confirm) {
                this.isLoading.next(false);
                return;
            }

            this.confirmReallyDeleteDialog.show().then((reallyConfirm) => {
                if (!reallyConfirm) {
                    this.isLoading.next(false);
                    return;
                }

                this.isLoading.next(true);

                this.employee
                    .delete()
                    .then(() => {
                        this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileMeatballPermanentlyDelete);
                        this.notify.success('employees.header.wasDeleted');
                        this.router.navigate(['/', 'employees', 'directory']);
                    })
                    .catch((err) => {
                        this.notify.error(err.message || 'employees.header.couldNotBeDeleted');
                        this.isLoading.next(false);
                    });
            });
        });
    }

    async onIssueRoe(): Promise<void> {
        await this.employeeRoeService.issueRoe(this.employee);
    }

    async onUndoTermination(): Promise<void> {
        this.isLoading.next(true);
        try {
            await this.terminationService.revokePendingTermination(
                this.employee,
                moment(this.employee.lastDayOfWorkOn).toDate()
            );
            // Currently do not emit these changes, need to reload the page
            window.location.reload();
        } catch (e) {
            this.notify.error(ErrorParser.parse(e));
            this.isLoading.next(false);
        }
    }

    async onUndoRehire(): Promise<void> {
        this.isLoading.next(true);

        try {
            await this.rehireService.revokePendingRehire(this.employee, moment(this.employee.nextDayOfWorkOn).toDate());
            // Currently do not emit these changes, need to reload the page
            window.location.reload();
        } catch (e) {
            this.notify.error(ErrorParser.parse(e));
            this.isLoading.next(false);
        }
    }

    openLeaveOfAbsenceModal(): void {
        this.isLeaveOfAbsenceDetailsModalOpen = true;
        this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileMeatballLeaveDetails);
    }

    closeLeaveOfAbsenceModal(): void {
        this.isLeaveOfAbsenceDetailsModalOpen = false;
    }

    navigateToLeaveOfAbsenceEdit(): void {
        this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileMeatballLeaveDetailsEdit);

        this.router.navigate(['../leave-of-absence'], {
            relativeTo: this.route,
            queryParams: { mode: 'edit' },
        });
    }

    canIssueRoe(): boolean {
        return this.employeeOnPayroll() && !this.employee?.isOnboarding();
    }

    /**
     * The user is returned to the previous URL they were on that was not within the employee's profile
     * This ensures that the back button doesn't bring them to a different tab, and instead returns them to a parent level page.
     */
    private getBackRoute(): string {
        const previousNonProfileRoute = this.routingStateService.firstPreviousUrlNotMatching(
            /\/employees\/directory\/\d+\/.*/
        );

        // Default URL from firstPreviousUrlNotMatching is dashboard, so we overwrite that with employee directory in the case that no previous URL matches (ie. refresh).
        return previousNonProfileRoute === '/dashboard' ? '/employees/directory' : previousNonProfileRoute;
    }

    private employeeOnPayroll(): boolean {
        return this.hasPayrollFeature && this.employee?.activeOnPayroll();
    }
}
